import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useActiveSection } from '../hooks/useActiveSection';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const activeSection = useActiveSection(['work', 'solutions', 'vision'], isHome);

  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const navItems = [
    { name: 'Work', section: 'work' },
    { name: 'Solutions', section: 'solutions' },
    { name: 'Vision', section: 'vision' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  /** True when this link points at whatever the reader is currently on. */
  const isActive = (item: { href?: string; section?: string }) =>
    item.href ? location.pathname === item.href : item.section === activeSection;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || isMenuOpen
          ? 'bg-surface/80 backdrop-blur-xl border-b border-surface-border'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img
              src="/nexalogo.png"
              alt="Nexasolutra"
              className="w-10 h-10 md:w-11 md:h-11 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <span className="font-display text-lg md:text-xl font-semibold text-white tracking-tight">
              Nexasolutra
            </span>
          </Link>

          {/* Centered nav */}
          <nav className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {navItems.map((item) => {
              const active = isActive(item);
              const classes = `nav-link ${
                active ? 'is-active text-white' : 'text-muted hover:text-white'
              } px-4 py-2 pb-3 text-sm font-medium rounded-lg transition-colors duration-300`;

              return item.href ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className={classes}
                  aria-current={active ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              ) : (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.section!)}
                  className={classes}
                  aria-current={active ? 'true' : undefined}
                >
                  {item.name}
                </button>
              );
            })}
          </nav>

          {/* CTA */}
          <div className="hidden md:block shrink-0">
            <Link to="/contact" className="btn-primary text-sm py-2.5 px-5">
              Book a Strategy Call
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-muted hover:text-white transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden pb-6 border-t border-surface-border pt-4 space-y-1 animate-fade-in">
            {navItems.map((item) => {
              const active = isActive(item);
              const classes = `nav-link-mobile block w-full text-left py-3 text-base font-medium transition-colors ${
                active ? 'is-active text-white' : 'text-muted hover:text-white'
              }`;

              return item.href ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className={classes}
                  aria-current={active ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              ) : (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.section!)}
                  className={classes}
                  aria-current={active ? 'true' : undefined}
                >
                  {item.name}
                </button>
              );
            })}
            <Link to="/contact" className="btn-primary w-full mt-4 text-center" onClick={() => setIsMenuOpen(false)}>
              Book a Strategy Call
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
